import React, { memo } from 'react';
import { StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Container, HStack, theme, useResponsive } from '@sweetgreen/garnish';

import {
  BagAccessButton,
  FeaturedChannels,
  PageWrapper,
  ReorderRail,
} from '@order/components';

import type { useHomeData, useHomeScreenLoadedContent } from '../../hooks';
import {
  useHomeFeaturedChannelsNavigation,
  useHomeNavigation,
} from '../../hooks';
import { HomeScreenHero } from '../HomeScreenHero';
import { HomeScreenMobileHeader } from '../HomeScreenMobileHeader';
import { HomeScreenWelcomePane } from '../HomeScreenWelcomePane';

export const HomeScreenContent = memo((props: HomeScreenContentProps) => {
  const {
    orders,
    status,
    deliveryRestaurant,
    deliveryVendor,
    cartOrderType,
    isCartEmpty,
    isLoggedIn,
    isLoading,
    isLoadingOrders,
    isLoadingStatus,
    hasErrors,
    loadedContent,
    orderPendingFeedbackId,
    onPressHeroCTA,
    onPressScanToPay,
  } = props;
  const { currentBreakpoint, match } = useResponsive();
  const gridColumnsNumber = currentBreakpoint.isMobile ? 1 : 2;
  const gridGap = match([0, theme.spacing.large, theme.spacing.xlarge]);
  const { handleGoToDefaultLocations } = useHomeNavigation();
  const onFeaturedChannelSelection = useHomeFeaturedChannelsNavigation();

  // ─── STYLES ─────────────────────────────────────────────────────────

  const containerStyles = [
    styles.containerWithGutters,
    match([styles.containerXS, styles.containerSM]),
  ];

  // ────────────────────────────────────────────────────────────────────

  return (
    <>
      <PageWrapper
        testID="home.container"
        style={styles.wrapper}
        contentContainerStyle={styles.scrollContainer}
      >
        <SafeAreaView edges={['top']}>
          <View style={containerStyles}>
            <Container>
              {currentBreakpoint.isMobile ? (
                <HomeScreenMobileHeader
                  onPressScanToPay={onPressScanToPay}
                  isLoggedIn={isLoggedIn}
                />
              ) : null}

              <HStack itemsPerRow={gridColumnsNumber} customSpacing={gridGap}>
                <View style={styles.column}>
                  {currentBreakpoint.isMobile ? (
                    <HomeScreenWelcomePane
                      isLoading={isLoading}
                      isLoadingStatus={isLoadingStatus}
                      hasCart={!isCartEmpty}
                      cartOrderType={cartOrderType}
                      data={status}
                      orderPendingFeedbackId={orderPendingFeedbackId}
                      seasonalHeroHeadlineText={loadedContent.heroHeadlineText}
                    />
                  ) : null}

                  <HomeScreenHero
                    isLoading={isLoading}
                    ctaText={loadedContent.heroCTA?.text}
                    imageURL={loadedContent.heroImageURL}
                    onPressCTA={onPressHeroCTA}
                  />
                </View>

                <View style={styles.column}>
                  {!currentBreakpoint.isMobile && (
                    <HomeScreenWelcomePane
                      isLoading={isLoading}
                      isLoadingStatus={isLoadingStatus}
                      hasCart={!isCartEmpty}
                      cartOrderType={cartOrderType}
                      data={status}
                      orderPendingFeedbackId={orderPendingFeedbackId}
                      seasonalHeroHeadlineText={loadedContent.heroHeadlineText}
                    />
                  )}
                  <FeaturedChannels
                    isLoadingOrders={isLoadingOrders}
                    deliveryRestaurant={deliveryRestaurant}
                    deliveryVendor={deliveryVendor}
                    onFeaturedChannelSelection={onFeaturedChannelSelection}
                    onNavigateToLocations={handleGoToDefaultLocations}
                  />
                </View>
              </HStack>
            </Container>
          </View>

          <View>
            <ReorderRail
              isLoading={isLoadingOrders}
              orders={orders}
              hasError={hasErrors}
            />
          </View>
        </SafeAreaView>
      </PageWrapper>
      {currentBreakpoint.isMobile ? <BagAccessButton /> : null}
    </>
  );
});

// ─── Styles ───────────────────────────────────────────────────────

const styles = StyleSheet.create({
  scrollContainer: {
    flexGrow: 1,
  },
  wrapper: {
    backgroundColor: theme.colors.KHAKI_3,
  },
  containerWithGutters: {
    flexGrow: 1,
    marginBottom: 'auto',
  },
  containerXS: {
    paddingTop: theme.spacing.medium,
    paddingBottom: theme.spacing.large,
  },
  containerSM: {
    paddingVertical: theme.spacing.xlarge,
  },
  column: {
    flexGrow: 1,
  },
});

// ─── Types ────────────────────────────────────────────────────────

type LoadedContent = ReturnType<typeof useHomeScreenLoadedContent>;

type HomeScreenContentProps = ReturnType<typeof useHomeData> & {
  readonly loadedContent: LoadedContent;
  readonly onPressHeroCTA: () => void;
  readonly onPressScanToPay: () => void;
};
